import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell from "../components/app/Shell";
import api, { fmtErr, avatarSrc } from "../lib/api";
import { toast } from "sonner";
import { User, Radar } from "lucide-react";
import { logSwallow } from "../lib/log";
import DebugProximity from "../components/app/DebugProximity";

export default function NearbyList() {
  const nav = useNavigate();
  const [users, setUsers] = useState([]);
  const [venue, setVenue] = useState(null);
  const [scope, setScope] = useState("ambient");
  const [scopeRadius, setScopeRadius] = useState(150);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (reping = false) => {
    try {
      if (reping && "geolocation" in navigator) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            async (pos) => {
              try {
                await api.post("/presence/ping", {
                  lat: pos.coords.latitude, lng: pos.coords.longitude,
                  mode: "anonymous", vibe_tag: "just vibing", radius_m: 150,
                  accuracy: pos.coords.accuracy,
                });
              } catch (e) { logSwallow("nearby.presence.ping", e); }
              resolve();
            },
            () => resolve(),
            { enableHighAccuracy: true, timeout: 7000 }
          );
        });
      }
      const { data } = await api.get("/scan/ambient");
      setUsers(data.users);
      setVenue(data.venue);
      setScope(data.scope);
      setScopeRadius(data.scope_radius_m);
    } catch (e) {
      toast.error(fmtErr(e));
      if (e?.response?.status === 400) nav("/scan");
    } finally { setLoading(false); }
  }, [nav]);
  useEffect(() => { load(); }, [load]);

  const tapIn = async (u) => {
    try {
      // For ambient (no venue), use a special venue_id namespace
      const vid = venue?.id || "__ambient__";
      const { data } = await api.post("/interests", { to_user: u.id, venue_id: vid });
      if (data.mutual) nav(`/match/${data.match.id}`);
      else toast.success(`I'm in sent to ${u.real_name || u.display_name || "them"}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Shell>
      <p className="font-mono-accent text-[#E60000]">{scope === "venue" ? `In ${venue?.name}` : "Street mode"}</p>
      <h1 className="font-display text-3xl mt-2 tracking-tighter">Nearby</h1>
      <p className="text-zinc-500 text-sm mt-1">{users.length} open within ~{scopeRadius}m</p>

      <div className="mt-4 flex gap-2">
        <button onClick={()=>load(true)} data-testid="rescan-btn" className="flex items-center gap-2 text-xs font-mono-accent text-[#E60000] hover:text-white transition">
          <Radar size={14} /> RE-SCAN
        </button>
      </div>

      <div className="mt-6 space-y-3" data-testid="nearby-list">
        {loading && <p className="text-zinc-500">Scanning…</p>}
        {!loading && users.length === 0 && (
          <div className="glass rounded-2xl p-6 text-center">
            <p className="text-zinc-400 text-sm">Nobody discoverable nearby yet.</p>
            <p className="text-zinc-500 text-xs mt-2 font-mono-accent">Density unlocks the magic. Re-scan in a busier spot.</p>
          </div>
        )}
        {users.map(u => (
          <div key={u.id} data-testid={`nearby-user-${u.id}`} className="glass rounded-2xl p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-[#1A1A1A] flex items-center justify-center border border-white/10">
              {avatarSrc(u) ? <img src={avatarSrc(u)} alt="" className="w-full h-full rounded-full object-cover" /> : <User size={20} className="text-zinc-500" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold truncate">{u.real_name || u.display_name || "someone"}</div>
              <div className="text-zinc-500 text-xs font-mono-accent">{u.vibe_tag} · {u.distance_band} · {u.distance_m}m</div>
            </div>
            <button data-testid={`imin-${u.id}`} onClick={()=>tapIn(u)} className="imin-btn px-4 py-2 text-sm font-semibold">I'M IN</button>
          </div>
        ))}
      </div>

      <DebugProximity />
    </Shell>
  );
}
